import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import ptLocale from '@fullcalendar/core/locales/pt'
import {
  holidaysPlugin,
  buildHolidaysSource,
  isHolidayEvent,
  HOLIDAYS_SOURCE_ID
} from './calendar/holidays_source'

const HOLIDAYS_PREF_KEY = 'calendar:show-holidays'

export default class extends Controller {
  static targets = ["calendar", "loading", "error", "holidaysToggle"]
  static values = {
    url: String,
    initialView: { type: String, default: 'dayGridMonth' },
    aspectRatio: { type: Number, default: 1.35 },
    eventColor: { type: String, default: '#4B5563' },
    timeZone: { type: String, default: 'Europe/Lisbon' }
  }

  static classes = ["loading", "error", "success"]

  connect() {
    this.wasMobile = this.isMobileDevice()
    this.showHolidays = this.readHolidaysPreference()
    this.initializeCalendar()
    this.createPopupElements()
    this.syncHolidaysToggle()
  }

  disconnect() {
    this.destroyCalendar()
    this.removePopupElements()
  }

  // Create popup elements
  createPopupElements() {
    // Create overlay
    this.overlay = document.createElement('div')
    this.overlay.className = 'calendar-popup-overlay'
    this.overlay.addEventListener('click', () => this.hidePopup())

    // Create popup
    this.popup = document.createElement('div')
    this.popup.className = 'calendar-popup'
    
    // Create popup content structure
    this.popup.innerHTML = `
      <div class="calendar-popup-header">
        <div class="calendar-popup-header-content">
          <div class="calendar-popup-status-indicator"></div>
          <h3 class="calendar-popup-title"></h3>
        </div>
        <button class="calendar-popup-close" aria-label="Fechar">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="calendar-popup-content">
        <div class="calendar-popup-info"></div>
        <div class="calendar-popup-description"></div>
        <div class="calendar-popup-actions">
          <a href="#" class="calendar-popup-button calendar-popup-button-primary" target="_blank">Abrir Tarefa</a>
          <button class="calendar-popup-button calendar-popup-button-secondary">Fechar</button>
        </div>
      </div>
    `

    // Add event listeners
    this.popup.querySelector('.calendar-popup-close').addEventListener('click', () => this.hidePopup())
    this.popup.querySelector('.calendar-popup-button-secondary').addEventListener('click', () => this.hidePopup())

    // Add to DOM
    document.body.appendChild(this.overlay)
    document.body.appendChild(this.popup)
  }

  // Remove popup elements
  removePopupElements() {
    if (this.overlay) this.overlay.remove()
    if (this.popup) this.popup.remove()
  }

  // Show popup with event details
  showPopup(event) {
    const title = this.extractEventTitle(event.title)
    const startDate = new Date(event.start)
    const extendedProps = event.extendedProps || {}
    // Use original end date for popup display (before eventDataTransform)
    const originalEndDate = extendedProps.original_end_date || event.end
    const endDate = originalEndDate ? new Date(originalEndDate) : null

    // Update popup header with status and title
    const statusIndicator = this.popup.querySelector('.calendar-popup-status-indicator')
    const statusColor = extendedProps.status_color || '#6b7280'
    const statusText = this.formatTaskStatus(extendedProps.status)
    
    // Generate darker color for gradient
    const darkerColor = this.darkenColor(statusColor, 20)
    
    statusIndicator.innerHTML = `
      <div class="status-badge" style="
        --status-color: ${statusColor}; 
        --status-color-dark: ${darkerColor};
        background: linear-gradient(135deg, ${statusColor}, ${darkerColor});
      ">
        <span class="status-badge-text">${statusText}</span>
      </div>
    `
    
    this.popup.querySelector('.calendar-popup-title').textContent = title
    this.popup.querySelector('.calendar-popup-info').innerHTML = `
      <div class="calendar-popup-info-item">
        <svg class="calendar-popup-info-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        <span>${startDate.toLocaleDateString('pt-PT', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })}</span>
      </div>
      ${endDate ? `
        <div class="calendar-popup-info-item">
          <svg class="calendar-popup-info-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <span>${endDate.toLocaleDateString('pt-PT', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
        </div>
      ` : ''}
      ${extendedProps.client ? `
        <div class="calendar-popup-info-item">
          <svg class="calendar-popup-info-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          <span>${this.escapeHtml(extendedProps.client)}</span>
        </div>
      ` : ''}
      ${extendedProps.total_amount ? `
        <div class="calendar-popup-info-item calendar-popup-payment-item">
          <div class="calendar-popup-payment-info">
            ${extendedProps.total_amount ? `
              <div class="calendar-popup-payment-row">
                <span class="calendar-popup-payment-label">Total</span>
                <span class="calendar-popup-payment-value">${this.formatCurrency(extendedProps.total_amount)}</span>
              </div>
            ` : ''}
            ${extendedProps.remaining_amount !== undefined ? `
              <div class="calendar-popup-payment-row">
                <span class="calendar-popup-payment-label">Restante</span>
                <span class="calendar-popup-payment-value">${this.formatCurrency(extendedProps.remaining_amount)}</span>
              </div>
            ` : ''}
          </div>
        </div>
      ` : ''}
      ${extendedProps.attachments?.length > 0 ? `
        <div class="calendar-popup-info-item">
          <div class="calendar-popup-attachments">
            <span class="calendar-popup-attachments-label">Ficheiros:</span>
            <div class="calendar-popup-attachments-list">
              ${extendedProps.attachments.map(attachment => `
                <a href="${attachment.url}" class="calendar-popup-attachment" target="_blank" rel="noopener noreferrer">
                  <svg class="calendar-popup-attachment-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <span class="calendar-popup-attachment-name">${this.escapeHtml(attachment.filename)}</span>
                </a>
              `).join('')}
            </div>
          </div>
        </div>
      ` : ''}
    `

    // Create content sections
    const contentSections = []

    // Add description if available
    if (extendedProps.description) {
      contentSections.push(`
        <div class="calendar-popup-section">
          <h4 class="calendar-popup-section-title">Observações</h4>
          <div class="calendar-popup-section-content">${this.escapeHtml(extendedProps.description)}</div>
        </div>
      `)
    }

    // Add materials if available
    if (extendedProps.materials) {
      contentSections.push(`
        <div class="calendar-popup-section">
          <h4 class="calendar-popup-section-title">Materiais</h4>
          <div class="calendar-popup-section-content">${this.escapeHtml(extendedProps.materials)}</div>
        </div>
      `)
    }

    // Update description container with all sections
    const descriptionEl = this.popup.querySelector('.calendar-popup-description')
    if (contentSections.length > 0) {
      descriptionEl.innerHTML = contentSections.join('')
      descriptionEl.style.display = 'block'
    } else {
      descriptionEl.style.display = 'none'
    }

    // Update task link
    const taskLink = this.popup.querySelector('.calendar-popup-button-primary')
    const taskUrl = extendedProps.task_url
    
    if (taskUrl) {
      taskLink.href = taskUrl
      taskLink.style.display = 'block'
      
      // Add click handler to open in new tab
      taskLink.onclick = (e) => {
        e.preventDefault()
        window.open(taskUrl, '_blank')
      }
    } else {
      taskLink.style.display = 'none'
    }

    // Show popup
    this.overlay.classList.add('active')
    this.popup.classList.add('active')
  }

  // Hide popup
  hidePopup() {
    this.overlay.classList.remove('active')
    this.popup.classList.remove('active')
  }

  // Initialize calendar with proper configuration
  initializeCalendar() {
    if (this.calendar) {
      this.destroyCalendar()
    }

    try {
      this.calendar = new Calendar(this.calendarTarget, this.getCalendarConfig())
      this.calendar.render()
    
      // Handle resize events for responsiveness
      this.resizeObserver = new ResizeObserver(this.handleResize.bind(this))
      this.resizeObserver.observe(this.calendarTarget)
      
    } catch (error) {
      console.error('Failed to initialize calendar:', error)
      this.showError('Erro ao inicializar o calendário')
    }
  }

  // Get comprehensive calendar configuration
  getCalendarConfig() {
    const isMobile = this.isMobileDevice()
    
    return {
      // Core plugins
      plugins: [dayGridPlugin, listPlugin, holidaysPlugin],
      
      // Localization
      locale: ptLocale,
      timeZone: this.timeZoneValue,
      
      // View configuration - responsive
      initialView: isMobile ? 'listMonth' : this.initialViewValue,
      aspectRatio: this.aspectRatioValue,
      height: 'auto',
      
      // Responsive breakpoints
      windowResizeDelay: 100,
      
      // Header configuration - responsive
      headerToolbar: isMobile ? {
        left: 'prev,next',
        center: 'title',
        right: 'today'
      } : {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,listMonth'
      },
      
      // View-specific settings
      views: {
        dayGridMonth: {
          titleFormat: { month: 'long', year: 'numeric' },
          fixedWeekCount: false,
          showNonCurrentDates: true,
          dayMaxEvents: 3, // Show "more" link after 3 events
          moreLinkClick: 'popover',
          moreLinkText: (num) => `+${num}`
        },
        listMonth: {
          titleFormat: { month: 'long', year: 'numeric' },
          listDayFormat: { weekday: 'long', month: 'long', day: 'numeric' },
          noEventsText: 'Não há tarefas para mostrar',
          listEventTimeFormat: { hour: 'numeric', minute: '2-digit', meridiem: false },
          eventTimeFormat: { hour: 'numeric', minute: '2-digit', meridiem: false },
          eventDisplay: 'block',
          eventContent: (arg) => {
            const { event } = arg
            const title = this.extractEventTitle(event.title)
            const truncatedTitle = this.smartTruncate(title, 50)
            
            return {
              html: `
                <div class="fc-event-main-frame" role="button" tabindex="0" aria-label="Tarefa: ${this.escapeHtml(event.title)}">
                  <div class="fc-event-content-wrapper">
                    <div class="fc-event-title-primary">${this.escapeHtml(truncatedTitle)}</div>
                    ${title !== truncatedTitle ? `<div class="fc-event-title-tooltip" data-full-title="${this.escapeHtml(title)}">...</div>` : ''}
                  </div>
                </div>
              `
            }
          }
        }
      },
      
      // Event source configuration
      eventSources: this.buildEventSources(),
      
      // Event handling
      loading: (isLoading) => this.handleLoading(isLoading),
      eventDidMount: (info) => this.handleEventDidMount(info),
      eventClick: (info) => this.handleEventClick(info),
      dateClick: (info) => this.handleDateClick(info),

      
      // Display options
      displayEventTime: false,
      displayEventEnd: false,
      eventDisplay: 'block',
      
      // Accessibility
      dayPopoverFormat: { month: 'long', day: 'numeric', year: 'numeric' },
      
      // Performance
      lazyFetching: true,

      // Error handling
      eventSourceFailure: (error) => this.showError('Erro ao carregar eventos do calendário')
    }
  }

  // Build the list of event sources, conditionally including holidays.
  buildEventSources() {
    const sources = [{
      id: 'tasks',
      url: this.urlValue || '/tasks/calendar_events',
      method: 'GET',
      extraParams: () => this.getExtraParams(),
      success: (content) => this.transformEvents(content),
      failure: (error) => this.handleEventSourceError(error),
      // Tasks API returns inclusive end dates; shift by +1 day so
      // multi-day events render correctly in FullCalendar (which uses
      // exclusive end dates). Holiday events from the iCal feed are
      // already exclusive and must not be shifted.
      eventDataTransform: (eventData) => this.normalizeTaskEndDate(eventData)
    }]

    if (this.showHolidays) {
      sources.push(buildHolidaysSource())
    }

    return sources
  }

  // Adjust an inclusive end date to FullCalendar's exclusive convention.
  normalizeTaskEndDate(eventData) {
    if (!eventData.end) return eventData

    if (!eventData.extendedProps) eventData.extendedProps = {}
    eventData.extendedProps.original_end_date = eventData.end

    const dateOnly = eventData.end.split('T')[0]
    const [year, month, day] = dateOnly.split('-').map(Number)
    const endDate = new Date(year, month - 1, day + 1)

    const formattedYear = endDate.getFullYear()
    const formattedMonth = String(endDate.getMonth() + 1).padStart(2, '0')
    const formattedDay = String(endDate.getDate()).padStart(2, '0')
    eventData.end = `${formattedYear}-${formattedMonth}-${formattedDay}`

    return eventData
  }

  // Toggle Portuguese holidays on/off and persist the preference.
  toggleHolidays() {
    this.showHolidays = !this.showHolidays
    this.persistHolidaysPreference()
    this.applyHolidaysSource()
    this.syncHolidaysToggle()
  }

  // Add or remove the holidays source on the live calendar without
  // rebuilding it from scratch.
  applyHolidaysSource() {
    if (!this.calendar) return

    const existing = this.calendar.getEventSourceById(HOLIDAYS_SOURCE_ID)

    if (this.showHolidays && !existing) {
      this.calendar.addEventSource(buildHolidaysSource())
    } else if (!this.showHolidays && existing) {
      existing.remove()
    }
  }

  // Read the user's holidays preference; defaults to true (show).
  readHolidaysPreference() {
    try {
      const stored = window.localStorage?.getItem(HOLIDAYS_PREF_KEY)
      if (stored === null || stored === undefined) return true
      return stored === 'true'
    } catch {
      return true
    }
  }

  persistHolidaysPreference() {
    try {
      window.localStorage?.setItem(HOLIDAYS_PREF_KEY, String(this.showHolidays))
    } catch {
      // Ignore storage errors (private mode, quota, etc.)
    }
  }

  // Reflect the current state on the toggle button, if present.
  syncHolidaysToggle() {
    if (!this.hasHolidaysToggleTarget) return

    const button = this.holidaysToggleTarget
    button.setAttribute('aria-pressed', String(this.showHolidays))
    button.dataset.active = String(this.showHolidays)
  }

  // Transform events with consistent styling.
  // Only applies to the tasks event source; the holidays ICS source
  // already provides its own colors and class names.
  transformEvents(events) {
    return events.map(event => ({
      ...event,
      backgroundColor: event.backgroundColor || this.eventColorValue,
      borderColor: event.borderColor || this.eventColorValue,
      textColor: '#FFFFFF',
      classNames: ['custom-event']
    }))
  }

  // Get extra parameters for API calls
  getExtraParams() {
        return {
      // Add any additional parameters needed for the API
      format: 'json'
    }
  }

  // Handle loading states
  handleLoading(isLoading) {
    if (isLoading) {
      this.showLoading()
    } else {
      this.hideLoading()
    }
  }

  // Enhanced event rendering with smart text presentation
  renderEventContent(arg) {
    const { event, view } = arg
    const title = this.extractEventTitle(event.title)
    const isListView = view.type.includes('list')
    const isSmallEvent = !isListView && arg.event.allDay
    
    // Smart text truncation based on context
    const truncatedTitle = this.smartTruncate(title, isListView ? 50 : 20)
    
    if (isListView) {
      // List view: More detailed information
      return {
        html: `
          <div class="fc-event-main-frame" role="button" tabindex="0" aria-label="Tarefa: ${this.escapeHtml(event.title)}">
            <div class="fc-event-content-wrapper">
              <div class="fc-event-title-primary">${this.escapeHtml(truncatedTitle)}</div>
              ${title !== truncatedTitle ? `<div class="fc-event-title-tooltip" data-full-title="${this.escapeHtml(title)}">...</div>` : ''}
            </div>
          </div>
        `
      }
    } else {
      // Month/Week view: Compact information
      return {
        html: `
          <div class="fc-event-main-frame" role="button" tabindex="0" aria-label="Tarefa: ${this.escapeHtml(event.title)}">
            <div class="fc-event-content-wrapper">
              <div class="fc-event-title-primary" data-full-title="${this.escapeHtml(title)}">${this.escapeHtml(truncatedTitle)}</div>
            </div>
          </div>
        `
      }
    }
  }

  // Event mounting with enhanced text handling
  handleEventDidMount(info) {
    const { el, event, view } = info
    
    // Add accessibility attributes
    el.setAttribute('role', 'button')
    el.setAttribute('tabindex', '0')
    el.setAttribute('aria-label', `Tarefa: ${event.title}`)
    
    // Enhanced tooltip with full information
    this.setupEventTooltip(el, event)
    
    // Add keyboard navigation
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.handleEventClick({ event, jsEvent: e })
      }
    })
    
    // Add enhanced hover effects with text reveal
    el.addEventListener('mouseenter', () => {
      this.showEventHover(el, event)
    })
    
    el.addEventListener('mouseleave', () => {
      this.hideEventHover(el)
    })
    
    // Ensure proper styling
    el.style.overflow = 'hidden'
    el.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  }

  // Smart text truncation utility
  smartTruncate(text, maxLength) {
    if (!text || text.length <= maxLength) return text
    
    // Try to break at word boundaries
    const truncated = text.substring(0, maxLength)
    const lastSpace = truncated.lastIndexOf(' ')
    
    if (lastSpace > maxLength * 0.7) {
      return truncated.substring(0, lastSpace) + '...'
    }
    
    return truncated + '...'
  }

  // Setup enhanced tooltip
  setupEventTooltip(el, event) {
    const title = this.extractEventTitle(event.title)
    el.title = title
  }

  // Enhanced hover effects
  showEventHover(el, event) {
    el.style.transform = 'scale(1.02) translateY(-1px)'
    el.style.zIndex = '15'
    el.style.boxShadow = '0 8px 25px rgba(8, 145, 178, 0.2)'
    
    // Show full text on hover for truncated items
    const titleEl = el.querySelector('.fc-event-title-primary')
    if (titleEl && titleEl.dataset.fullTitle) {
      titleEl.setAttribute('data-original-text', titleEl.textContent)
      titleEl.textContent = titleEl.dataset.fullTitle
    }
  }

  hideEventHover(el) {
    el.style.transform = ''
    el.style.zIndex = ''
    el.style.boxShadow = ''
    
    // Restore truncated text
    const titleEl = el.querySelector('.fc-event-title-primary')
    if (titleEl && titleEl.dataset.originalText) {
      titleEl.textContent = titleEl.dataset.originalText
      titleEl.removeAttribute('data-original-text')
    }
  }

  // Enhanced event click handling
  handleEventClick(info) {
    const { event, jsEvent } = info

    jsEvent.preventDefault()

    // Holidays are informational only — no popup.
    if (isHolidayEvent(event)) return

    this.showPopup(event)
  }

  // Handle date clicks for future enhancements
  handleDateClick(info) {
    // This can be used to add events or show date details
    console.log('Date clicked:', info.dateStr)
  }

  // Handle responsive layout changes
  handleResize() {
    if (!this.calendar) return
    
    const wasMobile = this.wasMobile || false
    const isMobile = this.isMobileDevice()
    
    // If mobile state changed, reinitialize calendar with new config
    if (wasMobile !== isMobile) {
      this.wasMobile = isMobile
      const currentDate = this.calendar.getDate()
      this.initializeCalendar()
      this.calendar.gotoDate(currentDate)
    } else {
      // Standard resize handling
      this.calendar.updateSize()
    }
  }

  // Detect mobile devices
  isMobileDevice() {
    return window.innerWidth <= 768 
  }

  // Error handling for event sources
  handleEventSourceError(error) {
    console.error('Event source error:', error)
    this.showError('Erro ao carregar eventos. Tente recarregar a página.')
  }

  // Extract event title without status
  extractEventTitle(fullTitle) {
    if (!fullTitle) return 'Tarefa sem título'
    return fullTitle.split(' - ')[0].trim()
  }

  escapeHtml(unsafe) {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  // Loading state management
  showLoading() {
    this.calendarTarget.classList.add('opacity-50', 'pointer-events-none')
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    this.calendarTarget.classList.remove('opacity-50', 'pointer-events-none')
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  // Error state management
  showError(message) {
    if (this.hasErrorTarget) {
      // Update error message in the error target
      const errorMessageEl = this.errorTarget.querySelector('[data-calendar-target="errorMessage"]')
      if (errorMessageEl) {
        errorMessageEl.textContent = message
      }
      this.errorTarget.classList.remove('hidden')
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        this.hideError()
      }, 5000)
    } else {
      // Fallback to console if no error target
      console.error(message)
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  // Public API methods
  refresh() {
    if (this.calendar) {
      this.calendar.refetchEvents()
    }
  }

  changeView(viewName) {
    if (this.calendar) {
      this.calendar.changeView(viewName)
    }
  }

  goToDate(date) {
    if (this.calendar) {
      this.calendar.gotoDate(date)
    }
  }

  // Cleanup
  destroyCalendar() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }

  // Format currency values
  formatCurrency(amount) {
    return new Intl.NumberFormat('pt-PT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  // Format payment status
  formatPaymentStatus(status) {
    const statusMap = {
      'paid': 'Pago',
      'partially_paid': 'Parcialmente Pago',
      'unpaid': 'Não Pago',
      'no_amount': 'Sem Valor Definido'
    }
    return statusMap[status] || status
  }

  // Format task status
  formatTaskStatus(status) {
    const statusMap = {
      'not_started': 'Não Iniciada',
      'started': 'Em Progresso',
      'blocked': 'Bloqueada',
      'done': 'Concluída'
    }
    return statusMap[status] || 'Desconhecido'
  }

  // Utility method to darken a hex color
  darkenColor(hex, percent) {
    // Remove # if present
    hex = hex.replace('#', '')
    
    // Parse r, g, b values
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    
    // Darken by percentage
    const factor = (100 - percent) / 100
    const newR = Math.round(r * factor)
    const newG = Math.round(g * factor)
    const newB = Math.round(b * factor)
    
    // Convert back to hex
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
  }
} 
